/**
 * ProviderDetailsContent Component
 *
 * Shared component that renders the hero section and tabbed content for provider profiles.
 *
 * This is intended to be a reusable building block for public and internal
 * provider profile experiences, ensuring consistent rendering while allowing
 * customization of interactive features via props.
 */

import { useMemo, useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../shared/components/ui/tabs';
import { ProviderDetailsHero } from './ProviderDetailsHero';
import { ProviderDetailsOverview } from './ProviderDetailsOverview';
import { ProviderDetailsServices } from './ProviderDetailsServices';
import { ProviderDetailsReviews } from './ProviderDetailsReviews';
import { ProviderDetailsQualifications } from './ProviderDetailsQualifications';
import { ProviderDetailsResources } from './ProviderDetailsResources';
import { ProviderDetailsCareDelivery } from './ProviderDetailsCareDelivery';
import { SkeletonGroup } from '../../../shared/components/SkeletonLoader';
import { getProviderDetailsSections, getDefaultTab } from '../utils/getSections';
import { formatServiceRange } from '../utils/formatters';
import type { ProviderProfile, ProviderService, ProviderBadge, ProviderMedia, GoogleReviewsData } from '../types/provider-profile.types';
import type { Resource } from '../hooks/useProviderResources';

export type ProviderDetailsContentProps = {
  // Provider data
  provider: ProviderProfile;
  isLoading?: boolean;

  // Optional lazy-loaded data
  servicesData?: { services: ProviderService[] } | null;
  servicesLoading?: boolean;
  qualificationsData?: {
    badges?: ProviderBadge[];
    expertise?: Array<{ id: string; name: string }>;
    specialties?: Array<{ id: string; name: string }>;
  } | null;
  qualificationsLoading?: boolean;
  mediaData?: { media: ProviderMedia[] } | null;
  mediaLoading?: boolean;
  reviewsData?: {
    googleReviews?: { reviews?: unknown[] } | null;
    averageRating?: number | null;
    totalReviewCount?: number;
  } | null;
  reviewsLoading?: boolean;
  reviewsError?: boolean;
  resourcesData?: Resource[] | null;
  resourcesLoading?: boolean;

  // Reviews summary for hero (can be different from full reviews data)
  displayRating?: number | null;
  displayReviewCount?: number;
  reviewsSummaryLoading?: boolean;

  // Action button controls
  canFavorite?: boolean;
  canAddToNetwork?: boolean;
  canReferProvider?: boolean;
  currentFavoriteState?: boolean;
  currentNetworkState?: boolean;
  isFavoriteLoading?: boolean;
  isNetworkLoading?: boolean;

  // Action handlers
  onFavoriteToggle?: () => void;
  onNetworkToggle?: () => void;
  onReferProvider?: () => void;
  /** Feature 071 — passed straight to the hero; absent means the button is hidden. */
  onShareContactCard?: () => void;
  onMessageProvider?: () => void;
  onShareProvider?: () => void;
  onRequestConsultation?: () => void;
  /** Feature 102 — secondary Calendly action, passed straight to the hero. */
  onScheduleConsultation?: () => void;

  // Optional hero media (for previews that need to show updated media)
  providerMedia?: ProviderMedia[];
};

export function ProviderDetailsContent({
  provider,
  isLoading = false,
  servicesData,
  servicesLoading = false,
  qualificationsData,
  qualificationsLoading = false,
  mediaData,
  mediaLoading = false,
  reviewsData,
  reviewsLoading = false,
  reviewsError = false,
  resourcesData,
  resourcesLoading = false,
  displayRating,
  displayReviewCount,
  reviewsSummaryLoading = false,
  canFavorite = false,
  canAddToNetwork = false,
  canReferProvider = false,
  currentFavoriteState = false,
  currentNetworkState = false,
  isFavoriteLoading = false,
  isNetworkLoading = false,
  onFavoriteToggle = () => {},
  onNetworkToggle = () => {},
  onReferProvider = () => {},
  onShareContactCard,
  onMessageProvider = () => {},
  onShareProvider = () => {},
  onRequestConsultation = () => {},
  onScheduleConsultation,
  providerMedia,
}: ProviderDetailsContentProps) {
  // Format service ranges for display
  const serviceRangesText = provider.serviceRanges?.map(formatServiceRange).join(' • ') || '';

  // Get available sections based on data (centralized configuration)
  const availableSections = useMemo(() => {
    return getProviderDetailsSections(provider, {
      servicesData,
      qualificationsData,
      mediaData,
      reviewsData,
      resourcesData,
    });
  }, [provider, servicesData, qualificationsData, mediaData, reviewsData, resourcesData]);

  // Get default active tab (first available section)
  const defaultTab = useMemo(() => {
    return getDefaultTab(availableSections);
  }, [availableSections]);

  // Controlled active tab state - resets when available sections change
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Reset active tab when defaultTab changes (e.g., after data loads and sections are filtered)
  useEffect(() => {
    // If current active tab is no longer in available sections, switch to default
    const isActiveTabValid = availableSections.some(section => section.id === activeTab);
    if (!isActiveTabValid) {
      setActiveTab(defaultTab);
    }
  }, [defaultTab, availableSections, activeTab]);

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      {isLoading ? (
        <SkeletonGroup.Hero />
      ) : (
        <ProviderDetailsHero
          provider={provider}
          providerMedia={providerMedia || mediaData?.media}
          displayRating={displayRating ?? provider.averageRating ?? null}
          displayReviewCount={displayReviewCount ?? provider.totalReviewCount ?? 0}
          reviewsLoading={reviewsSummaryLoading}
          serviceRangesText={serviceRangesText}
          canFavorite={canFavorite}
          canAddToNetwork={canAddToNetwork}
          canReferProvider={canReferProvider}
          currentFavoriteState={currentFavoriteState}
          currentNetworkState={currentNetworkState}
          onFavoriteToggle={onFavoriteToggle}
          onNetworkToggle={onNetworkToggle}
          onReferProvider={onReferProvider}
          onShareContactCard={onShareContactCard}
          onMessageProvider={onMessageProvider}
          onShareProvider={onShareProvider}
          onRequestConsultation={onRequestConsultation}
          onScheduleConsultation={onScheduleConsultation}
          isFavoriteLoading={isFavoriteLoading}
          isNetworkLoading={isNetworkLoading}
        />
      )}

      {/* Main Content Tabs - Controlled to handle dynamic sections */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          {availableSections.map(section => (
            <TabsTrigger key={section.id} value={section.id}>
              {section.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {availableSections.map(section => {
          // Render section content based on section ID
          let content;

          switch (section.id) {
            case 'overview':
              content = isLoading ? (
                <SkeletonGroup.Overview />
              ) : (
                <ProviderDetailsOverview
                  provider={provider}
                  serviceRangesText={serviceRangesText}
                />
              );
              break;

            case 'services':
              content = servicesLoading ? (
                <SkeletonGroup.Services />
              ) : (
                <ProviderDetailsServices provider={{ ...provider, services: servicesData?.services || [] }} />
              );
              break;

            case 'care-delivery':
              content = isLoading ? (
                <SkeletonGroup.Overview />
              ) : (
                <ProviderDetailsCareDelivery
                  priceDetails={provider.priceDetailsText}
                  carePhilosophy={provider.carePhilosophy}
                  emergencyResponseDetails={provider.emergencyResponseDetails}
                />
              );
              break;

            case 'qualifications':
              content = qualificationsLoading ? (
                <SkeletonGroup.Badges />
              ) : (
                <ProviderDetailsQualifications
                  provider={{
                    ...provider,
                    badges: qualificationsData?.badges || [],
                    expertise: qualificationsData?.expertise || [],
                    specialties: qualificationsData?.specialties || [],
                  }}
                />
              );
              break;

            case 'reviews':
              content = reviewsLoading ? (
                <SkeletonGroup.Overview />
              ) : reviewsError ? (
                <div className="text-center py-12">
                  <p className="text-gray-600 mb-4">Unable to load reviews at this time.</p>
                  <p className="text-sm text-gray-500">Google Places API may be temporarily unavailable.</p>
                </div>
              ) : (
                <ProviderDetailsReviews
                  provider={{
                    ...provider,
                    averageRating: reviewsData?.averageRating || null,
                    totalReviewCount: reviewsData?.totalReviewCount || 0,
                    googleReviews: (reviewsData?.googleReviews || null) as GoogleReviewsData,
                  }}
                />
              );
              break;

            case 'resources':
              // Feature 011 - US1: Resources tab (includes photos AND authored resources)
              content = (resourcesLoading || mediaLoading) ? (
                <SkeletonGroup.MediaGallery />
              ) : (
                <ProviderDetailsResources
                  providerId={provider.user.id}
                  photos={providerMedia ?? mediaData?.media ?? []}
                  documents={provider.documents ?? []}
                />
              );
              break;

            default:
              content = null;
          }

          return (
            <TabsContent key={section.id} value={section.id}>
              {content}
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
